import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Accessibility, 
  CheckCircle2, 
  Mail, 
  Phone,
  FileText,
  Settings,
  RefreshCw,
  MessageSquare
} from 'lucide-react';

const AccessibilityPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-hero py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
            <Accessibility className="w-4 h-4" />
            <span className="text-sm font-semibold">Accessibility</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            Website Accessibility Statement
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Last Updated: February 27, 2026
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            
            {/* Our Commitment */}
            <div>
              <h2 className="font-display text-2xl font-bold text-foreground mb-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                </div>
                Our Commitment to Accessibility
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                We are committed to ensuring digital accessibility for people with disabilities. We are 
                continually improving the user experience for everyone and applying relevant accessibility 
                standards to enhance usability and inclusivity. Our goal is to provide a website that is 
                accessible to the widest possible audience, regardless of technology or ability.
              </p>
            </div>

            {/* Accessibility Standards */}
            <div>
              <h2 className="font-display text-2xl font-bold text-foreground mb-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                Accessibility Standards
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA, 
                which are widely recognized as the industry standard for website accessibility. 
                Accessibility is an ongoing process, and we are actively working toward improving 
                the accessibility and usability of our website.
              </p>
            </div>

            {/* Technology & Tools */}
            <Card className="bg-gradient-subtle border-0">
              <CardContent className="p-8">
                <h2 className="font-display text-2xl font-bold text-foreground mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Settings className="w-5 h-5 text-primary" />
                  </div>
                  Accessibility Technology & Tools
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  To support our accessibility efforts, we have deployed industry-standard accessibility 
                  technology designed to enhance usability and assist visitors with disabilities. 
                  These tools may provide features such as:
                </p>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {[
                    'Screen reader enhancements',
                    'Keyboard navigation improvements',
                    'Color contrast adjustments',
                    'Text size and spacing controls',
                    'Focus indicators',
                    'Additional accessibility interface options'
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-foreground">
                      <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-6">
                  This technology helps us improve accessibility while we continue broader auditing, 
                  remediation, and development efforts.
                </p>
              </CardContent>
            </Card>

            {/* Ongoing Improvement */}
            <div>
              <h2 className="font-display text-2xl font-bold text-foreground mb-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-primary" />
                </div>
                Ongoing Improvement
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Accessibility is an ongoing initiative. We are:
              </p>
              <ul className="space-y-2 mb-4">
                {[
                  'Reviewing site content and functionality regularly',
                  'Working with developers and accessibility tools',
                  'Identifying and remediating barriers',
                  'Incorporating accessibility into future design and development'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                We understand accessibility is a continuous journey and are committed to making 
                improvements over time.
              </p>
            </div>

            {/* Third-Party Content */}
            <div>
              <h2 className="font-display text-2xl font-bold text-foreground mb-4">
                Third-Party Content
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Some areas of the website may include third-party content, plugins, or integrations 
                that are not fully controlled by us. While we encourage vendors to provide accessible 
                content, we cannot guarantee accessibility of third-party features. However, we 
                welcome feedback regarding any issues encountered.
              </p>
            </div>

            {/* Feedback & Assistance */}
            <Card className="bg-primary text-primary-foreground border-0">
              <CardContent className="p-8">
                <h2 className="font-display text-2xl font-bold mb-4 flex items-center gap-3">
                  <MessageSquare className="w-6 h-6" />
                  Feedback & Assistance
                </h2>
                <p className="text-primary-foreground/90 leading-relaxed mb-6">
                  If you experience difficulty accessing any part of this website, need assistance, 
                  or would like to request content in an alternative format, please contact us.
                </p>
                <p className="text-primary-foreground/90 leading-relaxed mb-4">
                  Please include:
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                    The page URL
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                    Description of the issue
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                    Your preferred contact method
                  </li>
                </ul>
                <div className="flex flex-wrap gap-4">
                  <Button variant="white" size="lg" asChild>
                    <a href="/contact">
                      <Mail className="w-5 h-5 mr-2" />
                      Contact Us
                    </a>
                  </Button>
                  <Button variant="heroOutline" size="lg" asChild>
                    <a href="tel:321-500-4263">
                      <Phone className="w-5 h-5 mr-2" />
                      321.500.4263
                    </a>
                  </Button>
                </div>
                <p className="text-primary-foreground/80 text-sm mt-6">
                  We aim to respond within a reasonable timeframe and will make reasonable efforts 
                  to provide accessible alternatives.
                </p>
              </CardContent>
            </Card>

            {/* Good Faith Efforts */}
            <div>
              <h2 className="font-display text-2xl font-bold text-foreground mb-4">
                Good Faith Efforts
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                We are making good-faith efforts to comply with accessibility standards and improve 
                usability for individuals with disabilities. Despite our efforts, some content may 
                not yet fully meet the strictest accessibility standards due to the evolving nature 
                of technology and ongoing updates. We appreciate your understanding as we continue 
                improving accessibility.
              </p>
            </div>

            {/* Terms & Conditions */}
            <div className="border-t border-border pt-8">
              <h2 className="font-display text-2xl font-bold text-foreground mb-4">
                Terms & Conditions — Accessibility Clause
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                We are committed to providing an accessible website experience for all users and 
                strive to follow recognized accessibility standards, including WCAG guidelines. We 
                have implemented industry-standard accessibility tools and continue to review, update, 
                and improve our website to enhance accessibility. Because accessibility is an ongoing 
                process, some areas of the website may not yet fully conform to all accessibility 
                standards. If you encounter accessibility barriers, please contact us so we can 
                provide assistance, offer alternative access, and address the issue where feasible. 
                Nothing in these Terms is intended to limit any rights available under applicable 
                accessibility laws.
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default AccessibilityPage;
